<script setup lang="ts">
import Modal from "@/ui/components/modal.vue";
import { onMounted, ref } from "vue";
import { useApiClient } from "@/api/client";
import { useExplorerStore } from "@/modules/explorer/store";
import useToast from "@/ui/composables/useToast";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { processedDocument } from "chartjs-graph";
import router from "@/router/router";

const shareLink = ref("");
const isLoading = ref(false);

const explorerStore = useExplorerStore();

const isDialogVisible = defineModel<boolean>();

const generateShareLink = async () => {
	shareLink.value = "";
	isLoading.value = true;

	const response = await promptInvalidPermissions(
		useApiClient("explorer").generateShareLink(
			processedDocument(explorerStore.document)
		)
	);

	if (response.isOk()) {
		// Resolve the graph route rather than the index, whose redirect would
		// drop the query.
		const { href } = router.resolve({
			name: "explorer.graphWrapper",
			query: { "shared-document": response.value.uuid },
		});
		shareLink.value = `${window.location.origin}${href}`;
	} else {
		useToast().error("Could not create a share link for this configuration");
	}

	isLoading.value = false;
};

const copyShareLink = async () => {
	await navigator.clipboard.writeText(shareLink.value);
	useToast().success("Share link copied to clipboard", {
		timeout: 2000,
	});
	isDialogVisible.value = false;
};

onMounted(() => generateShareLink());
</script>

<template>
	<Modal
		title="Share link"
		icon="fa: fa-solid fa-link"
		v-model:visible="isDialogVisible"
		width="600"
	>
		<span class="text-caption mb-2">
			Anyone in this environment can open this link to explore the current
			configuration. It does not save the configuration, and changes they make
			do not affect yours.
		</span>

		<v-text-field
			:model-value="shareLink"
			:loading="isLoading"
			readonly
			variant="outlined"
			density="compact"
			hide-details="auto"
			data-testid="explorer-share-link"
		/>

		<template v-slot:actions>
			<v-spacer />
			<v-btn color="red" @click="isDialogVisible = false">Cancel</v-btn>
			<v-btn
				variant="flat"
				color="secondary"
				:disabled="!shareLink"
				prepend-icon="mdi-content-copy"
				@click="copyShareLink"
			>
				Copy link
			</v-btn>
		</template>
	</Modal>
</template>

<style scoped lang="scss"></style>
